import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { Offer } from '../api/types';
import { money } from '../utils/format';

import { ChartTooltip } from './ChartTooltip';
import { Legend } from './Legend';
import { CHROME, SERIES } from './palette';

const COMPETITOR = '#1c5cab';
const OURS = SERIES[1];

/**
 * Ranked magnitude across shops. Our own offer is a different identity, not a
 * different magnitude, so it takes its own hue and is called out in the legend;
 * a dashed reference line marks our feed price even when we have no live offer.
 */
export function OfferBars({ offers, ourPrice }: { offers: Offer[]; ourPrice: number }) {
  const rows = offers
    .map((offer) => ({
      shopName: offer.shopName,
      price: Number(offer.price),
      shippingPrice: offer.shippingPrice === null ? null : Number(offer.shippingPrice),
      totalPrice: offer.totalPrice === null ? null : Number(offer.totalPrice),
      availability: offer.availability,
      shopRatingPercent: offer.shopRatingPercent,
      isOurShop: offer.isOurShop,
    }))
    .sort((left, right) => left.price - right.price);

  if (rows.length === 0) {
    return <div className="state">No offers captured for this product.</div>;
  }

  const hasOurOffer = rows.some((row) => row.isOurShop);

  return (
    <>
      <ResponsiveContainer width="100%" height={Math.max(180, rows.length * 30 + 40)}>
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 56, bottom: 4, left: 4 }} barCategoryGap={4}>
          <CartesianGrid stroke={CHROME.grid} horizontal={false} />
          <XAxis
            type="number"
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value: number) => `${value.toFixed(0)} €`}
          />
          <YAxis
            type="category"
            dataKey="shopName"
            tick={{ fill: CHROME.inkSecondary, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={170}
          />
          <ReferenceLine
            x={ourPrice}
            stroke={OURS}
            strokeDasharray="4 3"
            strokeWidth={2}
            label={{ value: 'our price', fill: CHROME.inkSecondary, fontSize: 10, position: 'top' }}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) {
                return null;
              }

              const row = payload[0].payload as (typeof rows)[number];

              return (
                <ChartTooltip
                  title={row.shopName}
                  rows={[
                    {
                      key: 'price',
                      label: 'Price',
                      value: money(row.price),
                      color: row.isOurShop ? OURS : COMPETITOR,
                    },
                    { key: 'ship', label: 'Shipping', value: money(row.shippingPrice) },
                    { key: 'total', label: 'With shipping', value: money(row.totalPrice) },
                    { key: 'avail', label: 'Availability', value: row.availability ?? '—' },
                    {
                      key: 'rating',
                      label: 'Shop rating',
                      value: row.shopRatingPercent === null ? '—' : `${row.shopRatingPercent}%`,
                    },
                  ]}
                />
              );
            }}
          />
          <Bar isAnimationActive={false} dataKey="price" radius={4} maxBarSize={20}>
            {rows.map((row) => (
              <Cell key={row.shopName} fill={row.isOurShop ? OURS : COMPETITOR} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <Legend
        entries={
          hasOurOffer
            ? [
                { label: 'Our offer', color: OURS },
                { label: 'Competitor', color: COMPETITOR },
              ]
            : [{ label: 'Competitor', color: COMPETITOR }]
        }
      />
    </>
  );
}
